import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import quickBooksOperationsApi from "../services/quickBooksOperationsApi";
import {
  QuickBooksOperation,
  QuickBooksOperationApiError,
  isActiveQuickBooksStatus,
} from "../types/quickBooksOperations";

const DEFAULT_POLL_DELAYS_MS = [1500, 3000, 6000, 12000, 30000];
const MAX_STATUS_FAILURES = 5;

type SourceType = "shipment" | "sun_order";

interface Options {
  enabled?: boolean;
  pollDelaysMs?: number[];
  onSucceeded?: (operation: QuickBooksOperation, context: { live: boolean }) => void;
  api?: Pick<
    typeof quickBooksOperationsApi,
    "getLatestShipment" | "getLatestSunOrder" | "getOperation" | "retry" | "replace"
  >;
}

export interface QuickBooksOperationsHookResult {
  operations: QuickBooksOperation[];
  loading: boolean;
  polling: boolean;
  mutatingId: string | null;
  error: QuickBooksOperationApiError | null;
  refresh: () => void;
  watch: (operations: QuickBooksOperation[]) => void;
  retry: (operation: QuickBooksOperation) => Promise<QuickBooksOperation | null>;
  replace: (operation: QuickBooksOperation) => Promise<QuickBooksOperation | null>;
}

function useQuickBooksOperations(
  sourceType: SourceType,
  sourceId: number | null | undefined,
  options: Options = {},
): QuickBooksOperationsHookResult {
  const api = options.api || quickBooksOperationsApi;
  const delays = useMemo(
    () =>
      options.pollDelaysMs?.length &&
      options.pollDelaysMs.every((delay) => Number.isFinite(delay) && delay > 0)
        ? options.pollDelaysMs
        : DEFAULT_POLL_DELAYS_MS,
    [options.pollDelaysMs],
  );
  const [operations, setOperations] = useState<QuickBooksOperation[]>([]);
  const [loading, setLoading] = useState(Boolean(sourceId));
  const [polling, setPolling] = useState(false);
  const [mutatingId, setMutatingId] = useState<string | null>(null);
  const [error, setError] = useState<QuickBooksOperationApiError | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const generation = useRef(0);
  const previousSource = useRef<string | null>(null);
  const liveOperations = useRef(new Set<string>());
  const completedOperations = useRef(new Set<string>());
  const callbackRef = useRef(options.onSucceeded);
  const mutationController = useRef<AbortController | null>(null);
  const mutationGeneration = useRef<number | null>(null);
  callbackRef.current = options.onSucceeded;

  const refresh = useCallback(() => setRefreshKey((value) => value + 1), []);
  const watch = useCallback(
    (next: QuickBooksOperation[]) => {
      next.forEach((operation) => liveOperations.current.add(operation.operation_id));
      setOperations(next);
      setError(null);
      refresh();
    },
    [refresh],
  );

  useEffect(() => {
    const currentGeneration = ++generation.current;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    let delayIndex = 0;
    let failures = 0;
    let known: QuickBooksOperation[] = [];
    const sourceKey = `${sourceType}:${sourceId ?? ""}`;
    const sourceChanged = previousSource.current !== sourceKey;
    previousSource.current = sourceKey;

    if (sourceChanged) {
      setOperations([]);
      liveOperations.current.clear();
      completedOperations.current.clear();
    }
    setError(null);
    setLoading(Boolean(sourceId));
    setPolling(false);

    if (!sourceId || options.enabled === false) {
      setLoading(false);
      return () => controller.abort();
    }

    const current = () => !stopped && generation.current === currentGeneration;
    const apply = (next: QuickBooksOperation[]) => {
      if (!current()) return;
      known = next;
      setOperations(next);
      next.forEach((operation) => {
        if (isActiveQuickBooksStatus(operation.status)) {
          liveOperations.current.add(operation.operation_id);
        }
        if (
          operation.status === "succeeded" &&
          !completedOperations.current.has(operation.operation_id)
        ) {
          completedOperations.current.add(operation.operation_id);
          callbackRef.current?.(operation, {
            live: liveOperations.current.has(operation.operation_id),
          });
        }
      });
    };
    const recover = (nextError: QuickBooksOperationApiError, again: () => void) => {
      if (!current() || nextError.isCanceled) return;
      setLoading(false);
      setError(nextError);
      const transient =
        nextError.status !== 401 &&
        nextError.status !== 403 &&
        (nextError.isNetworkError ||
          nextError.status === 429 ||
          (nextError.status !== undefined && nextError.status >= 500 && nextError.status <= 599));
      if (!transient || ++failures >= MAX_STATUS_FAILURES) {
        setPolling(false);
        return;
      }
      setPolling(true);
      const serverWait = Number.isFinite(nextError.retryAfter)
        ? Math.max(0, nextError.retryAfter!) * 1000
        : 0;
      const wait = Math.max(serverWait, delays[Math.min(delayIndex++, delays.length - 1)]);
      timer = setTimeout(() => current() && again(), Math.min(wait, 2147483647));
    };
    const poll = async (): Promise<void> => {
      const active = known.filter((operation) => isActiveQuickBooksStatus(operation.status));
      if (!current() || active.length === 0) {
        setPolling(false);
        return;
      }
      setPolling(true);
      try {
        const refreshed = await Promise.all(
          active.map((operation) => api.getOperation(operation.operation_id, controller.signal)),
        );
        if (!current()) return;
        const byDocument = new Map(known.map((operation) => [operation.document_type, operation]));
        refreshed.forEach((operation) => byDocument.set(operation.document_type, operation));
        failures = 0;
        setError(null);
        apply(Array.from(byDocument.values()));
        if (known.some((operation) => isActiveQuickBooksStatus(operation.status))) {
          timer = setTimeout(() => void poll(), delays[Math.min(delayIndex++, delays.length - 1)]);
        } else {
          setPolling(false);
        }
      } catch (caught) {
        recover(caught as QuickBooksOperationApiError, () => void poll());
      }
    };
    const loadLatest = async () => {
      try {
        const latest =
          sourceType === "shipment"
            ? (await api.getLatestShipment(sourceId, controller.signal)).operations
            : [await api.getLatestSunOrder(sourceId, controller.signal)].filter(
                (value): value is QuickBooksOperation => value !== null,
              );
        if (!current()) return;
        setLoading(false);
        failures = 0;
        setError(null);
        apply(latest);
        if (known.some((operation) => isActiveQuickBooksStatus(operation.status))) {
          void poll();
        } else {
          setPolling(false);
        }
      } catch (caught) {
        recover(caught as QuickBooksOperationApiError, () => void loadLatest());
      }
    };
    void loadLatest();

    return () => {
      stopped = true;
      controller.abort();
      if (timer) clearTimeout(timer);
      if (mutationGeneration.current === currentGeneration) {
        mutationController.current?.abort();
        mutationController.current = null;
        mutationGeneration.current = null;
        setMutatingId(null);
      }
    };
  }, [api, delays, options.enabled, refreshKey, sourceId, sourceType]);

  const mutate = useCallback(
    async (operation: QuickBooksOperation, action: "retry" | "replace") => {
      if (
        mutationController.current ||
        (action === "retry" ? !operation.can_retry : !operation.can_replace)
      ) {
        return null;
      }
      const controller = new AbortController();
      mutationController.current = controller;
      const currentMutationGeneration = generation.current;
      mutationGeneration.current = currentMutationGeneration;
      setMutatingId(operation.operation_id);
      try {
        const next = await api[action](operation.operation_id, controller.signal);
        if (generation.current !== currentMutationGeneration) return null;
        liveOperations.current.add(next.operation_id);
        setOperations((current) => [
          ...current.filter((item) => item.document_type !== next.document_type),
          next,
        ]);
        setError(null);
        refresh();
        return next;
      } catch (caught) {
        const nextError = caught as QuickBooksOperationApiError;
        if (!nextError.isCanceled && generation.current === currentMutationGeneration) {
          setError(nextError);
        }
        return null;
      } finally {
        if (generation.current === currentMutationGeneration) setMutatingId(null);
        if (mutationController.current === controller) mutationController.current = null;
        if (mutationGeneration.current === currentMutationGeneration) {
          mutationGeneration.current = null;
        }
      }
    },
    [api, refresh],
  );

  useEffect(
    () => () => {
      mutationController.current?.abort();
      mutationController.current = null;
      mutationGeneration.current = null;
    },
    [],
  );

  return {
    operations,
    loading,
    polling,
    mutatingId,
    error,
    refresh,
    watch,
    retry: (operation) => mutate(operation, "retry"),
    replace: (operation) => mutate(operation, "replace"),
  };
}

export function useSunShipmentQuickBooksOperations(
  shipmentId: number | null | undefined,
  options: Options = {},
) {
  return useQuickBooksOperations("shipment", shipmentId, options);
}

export function useSunOrderQuickBooksOperation(
  sunOrderId: number | null | undefined,
  options: Options = {},
) {
  return useQuickBooksOperations("sun_order", sunOrderId, options);
}
